import type {ImageType, TestContext} from "#webgl2/test/helpers/test-context"
import {expectException} from "#webgl2/test/helpers/helpers"
import type {Box2Like, Size2Like, Vector2Like} from "@cm/math"
import {Box2, Size2, Vector2, wrap} from "@cm/math"
import type {AddressMode, HalImageRef, ImageViewArgs} from "@cm/hal"

type ImageArgs = {
    type: ImageType
    size: Size2Like
    preSubImageRegion?: Box2Like
}

type ImagesArgs<ImageArgsType = ImageArgs> = {
    sourceImage: ImageArgsType
    resultImage: ImageArgsType
    addressMode: AddressMode
}

type BlitTestArgs = {
    imagesArgs: Omit<ImagesArgs<Omit<ImageArgs, "type">>, "addressMode">
    viewArgs: ImageViewArgs
    postSubImageRegion?: Box2Like
    shouldThrow?: boolean
}

export const runViewTests = (ctx: TestContext) => {
    console.log("Running view tests...")

    const imageSizeSmall: Size2Like = {width: 512, height: 512}
    const imageSizeLarge: Size2Like = {width: 1024, height: 1024}
    const allImageTypes: ImageType[] = ["physical", "virtual"]
    const allAddressModes: AddressMode[] = ["border", "wrap"]

    const allArgs: BlitTestArgs[] = [
        // sub-image views
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "sub-image",
                region: {x: -1, y: 128, width: 512, height: 768},
            },
            shouldThrow: true,
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "sub-image",
                region: {x: 384, y: -1, width: 512, height: 768},
            },
            shouldThrow: true,
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "sub-image",
                region: {x: 900, y: 128, width: 512, height: 768},
            },
            shouldThrow: true,
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "sub-image",
                region: {x: 384, y: 900, width: 512, height: 768},
            },
            shouldThrow: true,
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "sub-image",
                region: {x: 384, y: 128, width: 512, height: 768},
            },
        },

        // wrapped sub-image views
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "wrapped-sub-image",
                region: {x: -256, y: -384, width: 512, height: 512},
            },
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "wrapped-sub-image",
                region: {x: -1280, y: -1408, width: 512, height: 512},
            },
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "wrapped-sub-image",
                region: {x: 768, y: 1536, width: 512, height: 512},
            },
        },
        // dilated wrapped sub-image views
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "dilated-wrapped-sub-image",
                region: {x: 256, y: 384, width: 768, height: 768},
            },
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeLarge,
                },
                resultImage: {
                    size: imageSizeSmall,
                },
            },
            viewArgs: {
                type: "dilated-wrapped-sub-image",
                region: {x: 256, y: 384, width: 768, height: 768},
            },
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeSmall,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "dilated-wrapped-sub-image",
                region: {x: 256, y: 384, width: 768, height: 768},
            },
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeSmall,
                },
                resultImage: {
                    size: imageSizeLarge,
                },
            },
            viewArgs: {
                type: "dilated-wrapped-sub-image",
                region: {x: 256, y: 384, width: 768, height: 768},
            },
            postSubImageRegion: {x: 128, y: 0, width: 512, height: 640},
        },
        {
            imagesArgs: {
                sourceImage: {
                    size: imageSizeSmall,
                },
                resultImage: {
                    size: imageSizeSmall,
                },
            },
            viewArgs: {
                type: "dilated-wrapped-sub-image",
                region: {x: 256, y: 384, width: 768, height: 768},
            },
        },
    ]
    for (const imageType of allImageTypes) {
        for (const addressMode of allAddressModes) {
            for (const args of allArgs) {
                console.log("Running view tests for args", args)
                runViewBlitTests(
                    ctx,
                    {
                        ...args.imagesArgs,
                        sourceImage: {
                            ...args.imagesArgs.sourceImage,
                            type: imageType,
                        },
                        resultImage: {
                            ...args.imagesArgs.resultImage,
                            type: imageType,
                        },
                        addressMode: addressMode,
                    },
                    args.viewArgs,
                    args.postSubImageRegion,
                    args.shouldThrow ?? false,
                )
            }
        }
    }
}

const runViewBlitTests = (ctx: TestContext, imageArgs: ImagesArgs, viewArgs: ImageViewArgs, postSubImageRegion: Box2Like | undefined, shouldThrow: boolean) => {
    console.log("Testing blitting from view")
    testViewBlit(ctx, {
        shouldThrow,
        ...imageArgs,
        sourceImage: {
            ...imageArgs.sourceImage,
            view: {
                ...viewArgs,
                postSubImageRegion,
            },
        },
    })

    console.log("Testing blitting to view")
    if (imageArgs.resultImage.type === "virtual") {
        console.warn("Skipping blitting to view for virtual images, as it is buggy and needs to be fixed first.")
    } else if (viewArgs.type === "dilated-wrapped-sub-image" && postSubImageRegion) {
        console.warn("Skipping blitting to dilated wrapped sub-image view with postSubImageRegion, as it has known issues.")
    } else {
        testViewBlit(ctx, {
            shouldThrow,
            ...imageArgs,
            resultImage: {
                ...imageArgs.resultImage,
                view: {
                    ...viewArgs,
                    postSubImageRegion,
                },
            },
        })
    }

    console.log("Testing blitting from and to view")
    if (imageArgs.resultImage.type === "virtual") {
        console.warn("Skipping blitting to view for virtual images, as it is buggy and needs to be fixed first.")
    } else if (viewArgs.type === "dilated-wrapped-sub-image" && postSubImageRegion) {
        console.warn("Skipping blitting to dilated wrapped sub-image view with postSubImageRegion, as it has known issues.")
    } else {
        testViewBlit(ctx, {
            shouldThrow,
            ...imageArgs,
            sourceImage: {
                ...imageArgs.sourceImage,
                view: {
                    ...viewArgs,
                    postSubImageRegion,
                },
            },
            resultImage: {
                ...imageArgs.resultImage,
                view: {
                    ...viewArgs,
                    postSubImageRegion,
                },
            },
        })
    }
}

const testViewBlit = (
    ctx: TestContext,
    args: {
        sourceImage: {
            type: ImageType
            size: Size2Like
            view?: ImageViewArgs & {postSubImageRegion?: Box2Like}
        }
        resultImage: {
            type: ImageType
            size: Size2Like
            view?: ImageViewArgs & {postSubImageRegion?: Box2Like}
        }
        addressMode: AddressMode
        shouldThrow: boolean
    },
) => {
    const exec = () => {
        let sourceImage: HalImageRef | undefined
        let resultImage: HalImageRef | undefined
        try {
            // create source test pattern image
            sourceImage = ctx.createTestPatternImage({
                ...args.sourceImage.size,
                type: args.sourceImage.type,
            })

            // create result image
            const clearColor = {r: -1, g: -2, b: -3, a: -4}
            resultImage = ctx.createConstantImage({
                ...args.resultImage.size,
                type: args.resultImage.type,
                constant: clearColor,
            })

            const createView = (image: HalImageRef, viewArgs?: ImageViewArgs & {postSubImageRegion?: Box2Like}) => {
                if (!viewArgs) {
                    return image.ref
                }
                let view = image.ref.createView(viewArgs)
                if (viewArgs.postSubImageRegion) {
                    view = view.createView({
                        type: "sub-image",
                        region: viewArgs.postSubImageRegion,
                    })
                }
                return view
            }

            // create source view
            const blitSource = createView(sourceImage, args.sourceImage.view)

            // create result view
            const blitTarget = createView(resultImage, args.resultImage.view)

            // blit to result image
            const wrappingSource = args.addressMode === "wrap"
            if (wrappingSource) {
                ctx.blitFullTarget({
                    target: blitTarget,
                    source: blitSource,
                    addressMode: args.addressMode,
                })
            } else {
                ctx.blit({
                    target: blitTarget,
                    source: blitSource,
                })
            }

            // check the result
            const getRegion = (args: {size: Size2Like; view?: ImageViewArgs & {postSubImageRegion?: Box2Like}}) => {
                const region = args.view?.region ?? Box2.fromSize(args.size)
                return region
            }
            const applyPostSubImageRegion = (region: Box2Like, postSubImageRegion?: Box2Like) => {
                if (postSubImageRegion) {
                    if (
                        postSubImageRegion.x < 0 ||
                        postSubImageRegion.y < 0 ||
                        postSubImageRegion.x + postSubImageRegion.width > region.width ||
                        postSubImageRegion.y + postSubImageRegion.height > region.height
                    ) {
                        throw new Error(`Invalid postSubImageRegion ${JSON.stringify(postSubImageRegion)} for region ${JSON.stringify(region)}`)
                    }
                    region = {
                        x: region.x + postSubImageRegion.x,
                        y: region.y + postSubImageRegion.y,
                        width: postSubImageRegion.width,
                        height: postSubImageRegion.height,
                    }
                }
                return region
            }
            let sourceRegion = getRegion(args.sourceImage)
            let resultRegion = getRegion(args.resultImage)
            sourceRegion = {
                ...Vector2.wrap(sourceRegion, args.sourceImage.size),
                width: sourceRegion.width,
                height: sourceRegion.height,
            }
            resultRegion = {
                ...Vector2.wrap(resultRegion, args.resultImage.size),
                width: resultRegion.width,
                height: resultRegion.height,
            }
            const sourceWrapSize = args.sourceImage.view?.type !== "sub-image" ? args.sourceImage.size : undefined
            const sourceGapSize = Size2.max(new Size2(0, 0), Size2.sub(sourceRegion, args.sourceImage.size))
            const resultGapSize = Size2.max(new Size2(0, 0), Size2.sub(resultRegion, args.resultImage.size))
            sourceRegion = applyPostSubImageRegion(sourceRegion, args.sourceImage.view?.postSubImageRegion)
            resultRegion = applyPostSubImageRegion(resultRegion, args.resultImage.view?.postSubImageRegion)
            const writtenRegion = Box2.fromPositionAndSize(resultRegion, wrappingSource ? resultRegion : Size2.min(sourceRegion, resultRegion))
            let remainingRegions = [Box2.fromSize(args.resultImage.size)]
            let pixelsTested = 0
            const testSubRegion = (args: {region: Box2Like; expectedOffset: Vector2Like}) => {
                if (args.region.width <= 0 || args.region.height <= 0) {
                    return
                }
                // this loop is to test if the is source wrapping correctly
                for (let testOfsY = 0; testOfsY < args.region.height; testOfsY += sourceRegion.height) {
                    for (let testOfsX = 0; testOfsX < args.region.width; testOfsX += sourceRegion.width) {
                        const testRegion = {
                            x: args.region.x + testOfsX,
                            y: args.region.y + testOfsY,
                            width: Math.min(sourceRegion.width, args.region.width - testOfsX),
                            height: Math.min(sourceRegion.height, args.region.height - testOfsY),
                        }
                        ctx.expectTestPattern({
                            image: resultImage!.ref,
                            region: testRegion,
                            expectedOffset: args.expectedOffset,
                            wrapSize: sourceWrapSize,
                            gapSize: sourceGapSize,
                        })
                        pixelsTested += testRegion.width * testRegion.height
                    }
                }
                remainingRegions = remainingRegions.flatMap((remainingRegion) => Box2.sub(remainingRegion, args.region))
            }
            const expectedOffset = {
                x: wrap(sourceRegion.x, args.sourceImage.size.width) + resultRegion.x - writtenRegion.x,
                y: wrap(sourceRegion.y, args.sourceImage.size.height) + resultRegion.y - writtenRegion.y,
            }
            // nominal
            testSubRegion({
                region: {
                    x: resultRegion.x,
                    y: resultRegion.y,
                    width: Math.min(writtenRegion.width, args.resultImage.size.width - resultRegion.x),
                    height: Math.min(writtenRegion.height, args.resultImage.size.height - resultRegion.y),
                },
                expectedOffset: {
                    x: expectedOffset.x,
                    y: expectedOffset.y,
                },
            })
            // wrapping in x
            testSubRegion({
                region: {
                    x: 0,
                    y: resultRegion.y,
                    width: resultRegion.x + writtenRegion.width - args.resultImage.size.width - resultGapSize.width,
                    height: Math.min(writtenRegion.height, args.resultImage.size.height - resultRegion.y),
                },
                expectedOffset: {
                    x: expectedOffset.x + args.resultImage.size.width - resultRegion.x + resultGapSize.width,
                    y: expectedOffset.y,
                },
            })
            // wrapping in y
            testSubRegion({
                region: {
                    x: resultRegion.x,
                    y: 0,
                    width: Math.min(writtenRegion.width, args.resultImage.size.width - resultRegion.x),
                    height: resultRegion.y + writtenRegion.height - args.resultImage.size.height - resultGapSize.height,
                },
                expectedOffset: {
                    x: expectedOffset.x,
                    y: expectedOffset.y + args.resultImage.size.height - resultRegion.y + resultGapSize.height,
                },
            })
            // wrapping in both x and y
            testSubRegion({
                region: {
                    x: 0,
                    y: 0,
                    width: resultRegion.x + writtenRegion.width - args.resultImage.size.width - resultGapSize.width,
                    height: resultRegion.y + writtenRegion.height - args.resultImage.size.height - resultGapSize.height,
                },
                expectedOffset: {
                    x: expectedOffset.x + args.resultImage.size.width - resultRegion.x + resultGapSize.width,
                    y: expectedOffset.y + args.resultImage.size.height - resultRegion.y + resultGapSize.height,
                },
            })
            // check remaining regions
            for (const remainingRegion of remainingRegions) {
                ctx.expectConstant({
                    image: resultImage.ref,
                    region: remainingRegion,
                    expectedConstant: clearColor,
                })
                pixelsTested += remainingRegion.width * remainingRegion.height
            }
            if (pixelsTested !== resultImage.ref.width * resultImage.ref.height) {
                throw new Error(`Expected to test ${resultImage.ref.width * resultImage.ref.height} pixels, but tested ${pixelsTested} pixels instead.`)
            }
        } finally {
            // clean up
            sourceImage?.release()
            resultImage?.release()
        }
    }
    if (args.shouldThrow) {
        expectException(exec, "Blit with view should throw")
    } else {
        exec()
    }
}
